import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { FISCAL_ROUTES, apiMessage, newOperationId } from '../fiscalApi';

interface Props {
  open: boolean;
  onClose: () => void;
  onBuilt: () => void;
}

/**
 * `POST /fiscal/checks/:checkId/document` — the recovery path named in
 * `FiscalCheckoutListener`'s catch (`docs/fiscal.md` "Resolving a problem"):
 * when queueing failed at checkout because the venue was misconfigured or the
 * database blinked, the sale was still banked and this is how it gets
 * reported. Also how a venue back-fills sales taken before it configured
 * Egypt at all — so a check id is asked for directly rather than offering a
 * picker scoped to "checks with no document", which would hide exactly the
 * back-fill case this exists for.
 */
export default function BuildDocumentDialog({ open, onClose, onBuilt }: Props) {
  const { t } = useTranslation();
  const [checkId, setCheckId] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const id = checkId.trim();
    if (!id) return;
    setBusy(true);
    try {
      await api.post(FISCAL_ROUTES.buildForCheck(id), null, {
        headers: { 'X-Operation-Id': newOperationId('fiscal-build') },
      });
      toast.success(t('fiscal.build.success'));
      setCheckId('');
      onBuilt();
      onClose();
    } catch (e) {
      toast.error(apiMessage(e, t('fiscal.build.failed')));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('fiscal.build.title')}
      description={t('fiscal.build.description')}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button onClick={submit} disabled={busy || !checkId.trim()}>
            {t('fiscal.build.submit')}
          </Button>
        </>
      }
    >
      <Field label={t('fiscal.build.checkId')} hint={t('fiscal.build.checkIdHint')} controlId="fiscal-build-check-id">
        <Input
          value={checkId}
          onChange={(e) => setCheckId(e.target.value)}
          placeholder={t('fiscal.build.checkIdPlaceholder')}
          autoFocus
        />
      </Field>
    </Dialog>
  );
}
